/**
 * AccessOverviewModal — who can see a dashboard, card by card.
 *
 * Answers the question the share panel cannot: it is scoped to one node, so a
 * grant made two levels down is invisible to it. This shows the whole subtree
 * at once, and picking a principal traces exactly what they see.
 *
 * It is a navigator, not an editor. A row opens that card's own share panel,
 * which already implements the rules — the inherited level is the floor, and an
 * inherited-only user can be raised but not lowered.
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  BuildingsIcon,
  CaretRightIcon,
  FunnelIcon,
  InfoIcon,
  KeyIcon,
  LockSimpleIcon,
  UserCircleDashedIcon,
  UserCirclePlusIcon,
  UsersIcon
} from '@phosphor-icons/react';
import Tooltip from 'src/components/ui/Tooltip';
import clsx from 'clsx';

import Modal from 'src/components/ui/Modal';
import Banner from 'src/components/ui/Banner';
import Button from 'src/components/ui/Button';
import Dropdown from 'src/components/ui/Dropdown';
import Loading from 'src/components/ui/Loading';
import Popover from 'src/components/ui/Popover';
import UserAvatar from 'src/components/ui/UserAvatar';

import { useAccessOverview } from '../api/hooks';
import type { AccessCard, AccessPrincipal, DashboardPermission } from '../api/types';
import {
  ALL_USERS_PRINCIPAL_ID,
  cardsById,
  defaultExpanded,
  effectiveAccess,
  flattenCards,
  principalsInPlay,
  roleLabel,
  sourcesOf,
  summariseLevels,
  summariseCard
} from '../accessOverview';

interface AccessOverviewModalProps {
  open: boolean;
  onClose: () => void;
  /** The dashboard or group card to show access for. */
  nodeId: string;
  nodeName: string;
  /** Opens the share panel for one card. Only ever called for shareable cards. */
  onManageCard?: (card: AccessCard) => void;
}

const EVERYONE = '__everyone__';
const HELP_DISMISSED_KEY = 'access-overview-help-dismissed';

const TABLE_GRID = 'grid grid-cols-[minmax(0,1fr)_8.5rem_7.5rem_2.25rem] items-center gap-3 px-3';

function HoverTip({ title, children }: { title?: ReactNode; children: ReactElement }) {
  if (!title) return children;
  return (
    <Tooltip title={title} arrow placement="top" enterDelay={150}>
      {children}
    </Tooltip>
  );
}

/** Chip anatomy shared with the PAM tree: fixed width so the column lines up. */
function LevelPill({
  label,
  tone,
  title
}: {
  label: string;
  tone: 'granted' | 'inherited' | 'none' | 'mixed';
  title?: string;
}) {
  return (
    <HoverTip title={title}>
      <span
        className={clsx(
          'inline-flex min-w-24 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium',
          tone === 'granted' && 'border border-border-subtle bg-slate-100 text-content-default',
          tone === 'inherited' && 'border border-dashed border-slate-300 bg-white text-content-subtle',
          tone === 'mixed' && 'border border-slate-300 bg-slate-50 text-content-default',
          tone === 'none' && 'border border-slate-100 bg-white text-slate-400'
        )}
      >
        {label}
      </span>
    </HoverTip>
  );
}

/**
 * What the access-level pills mean, behind the same Key control the dashboard
 * uses for assurance colours. A line of copy under the table was competing
 * with the help banner; the encodings belong in a key.
 */
const ACCESS_KEY_ITEMS: { label: string; tone: 'granted' | 'inherited' | 'mixed'; meaning: string }[] = [
  { label: 'Edit', tone: 'granted', meaning: 'Access is set on this card' },
  { label: 'Edit', tone: 'inherited', meaning: 'Inherited from a card above' },
  { label: 'View → Owner', tone: 'mixed', meaning: 'Different people have different access levels' }
];

function AccessKey() {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <span ref={anchorRef} className="inline-flex shrink-0">
        <Button
          variant="outline"
          size="sm"
          icon={KeyIcon}
          aria-label="Access key"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        />
      </span>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        placement="bottom-end"
        offset={[0, 8]}
        className="p-3"
      >
        <p className="mb-2 text-xs font-semibold text-content-default">Access levels</p>
        <ul className="space-y-2">
          {ACCESS_KEY_ITEMS.map((item) => (
            <li key={item.meaning} className="flex items-center gap-2 whitespace-nowrap text-sm text-content-default">
              <LevelPill label={item.label} tone={item.tone} />
              <span>{item.meaning}</span>
            </li>
          ))}
        </ul>
      </Popover>
    </>
  );
}

/**
 * One holder. The tooltip comes from the caller because the avatar alone cannot
 * say what this holder can do — that is the question the stack raises.
 */
function PrincipalAvatar({ principal, title }: { principal: AccessPrincipal; title: string }) {
  if (principal.kind === 'person') {
    return (
      <HoverTip title={title}>
        <span className="inline-flex shrink-0">
          <UserAvatar name={principal.name} email={principal.email} size="sm" />
        </span>
      </HoverTip>
    );
  }
  // A group and the organisation both hold access without being a person, and
  // they used to render the same glyph — so the only thing separating "one team"
  // from "everyone here" was a hover title.
  const isOrganisation = principal.kind === 'organisation';
  return (
    <HoverTip title={title}>
      <span
        className={clsx(
          'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-content-default',
          isOrganisation ? 'bg-indigo-100' : 'bg-teal-100'
        )}
      >
        {isOrganisation ? <BuildingsIcon size={15} aria-hidden /> : <UsersIcon size={15} aria-hidden />}
      </span>
    </HoverTip>
  );
}

/**
 * What one avatar means, in a sentence: who, what they can do, and — for a
 * group — that the level is its ceiling rather than every member's.
 */
function holderTooltip(
  principal: AccessPrincipal,
  access: { role: DashboardPermission; grantedHere: boolean }
): string {
  const level = roleLabel(access.role);
  const where = access.grantedHere ? 'granted on this card' : 'inherited from a card above';
  if (principal.kind === 'organisation') {
    // An all-users share is not limited to this organisation, so it must not
    // borrow the tenant share's wording — its own name states its audience.
    return principal.id === ALL_USERS_PRINCIPAL_ID
      ? `${principal.name} can ${level.toLowerCase()} this card (${where})`
      : `${principal.name}: everyone in the organisation can ${level.toLowerCase()} this card (${where})`;
  }
  if (principal.kind === 'group') {
    return `${principal.name} (group): up to ${level} — a member's own level depends on their role in the group (${where})`;
  }
  return `${principal.name}: ${level}, ${where}`;
}

export default function AccessOverviewModal({
  open,
  onClose,
  nodeId,
  nodeName,
  onManageCard
}: AccessOverviewModalProps) {
  const { data, isLoading, isError, refetch } = useAccessOverview(nodeId, open);
  const [focus, setFocus] = useState<string>(EVERYONE);
  const [expanded, setExpanded] = useState<Set<string> | null>(null);
  const [helpDismissed, setHelpDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(HELP_DISMISSED_KEY) === 'true';
  });
  const accessForId = useId();

  // The modal stays mounted while the user moves between a dashboard and its
  // groups, so state from the previous subtree has to go: a stale expansion set
  // will not contain the new root, which would collapse every row under it.
  useEffect(() => {
    setExpanded(null);
    setFocus(EVERYONE);
  }, [nodeId]);

  const byId = useMemo(() => cardsById(data?.cards ?? []), [data]);
  const principals = useMemo(() => principalsInPlay(data?.grants ?? []), [data]);
  const focused = principals.find((p) => p.id === focus) ?? null;
  const focusOptions = useMemo(
    () => [
      { value: EVERYONE, label: 'Everyone' },
      ...principals.map((principal) => ({ value: principal.id, label: principal.name }))
    ],
    [principals]
  );

  const openCards = expanded ?? (data ? defaultExpanded(data) : new Set<string>());
  const rows = useMemo(
    () => flattenCards(data?.cards ?? [], openCards),
    // openCards is derived from state or the response; both are in the deps.
    [data, openCards]
  );

  const toggle = (cardId: string) => {
    const next = new Set(openCards);
    if (next.has(cardId)) {
      next.delete(cardId);
    } else {
      next.add(cardId);
    }
    setExpanded(next);
  };

  const cardCount = (data?.cards.length ?? 1) - 1;

  const filterControl =
    !isLoading && data && !isError ? (
      <div className="flex items-center gap-1.5">
        <FunnelIcon size={16} className="shrink-0 text-content-subtle" aria-hidden />
        <label htmlFor={accessForId} className="sr-only">
          Filter by
        </label>
        <Dropdown
          id={accessForId}
          options={focusOptions}
          value={focus}
          onChange={(value) => setFocus(String(value))}
          size="sm"
          width="w-32 sm:w-52"
          placeholder="Everyone"
          ariaLabel="Filter by person or group"
        />
      </div>
    ) : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="w-full max-w-4xl"
      headerClassName="gap-3 items-start max-sm:flex-col max-sm:items-stretch"
      headerActions={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {filterControl}
          <AccessKey />
        </div>
      }
      title={
        <div className="flex flex-col gap-0.5">
          <h2 className="text-lg font-semibold text-content-default">Access overview</h2>
          <p className="text-sm text-content-subtle">
            {nodeName}
            {cardCount > 0 && ` · ${cardCount} ${cardCount === 1 ? 'card' : 'cards'}`}
          </p>
        </div>
      }
    >
      {isLoading ? (
        <Loading text="Loading access..." />
      ) : isError || !data ? (
        <div className="flex flex-col items-start gap-3 px-1 py-4">
          <p className="text-sm text-content-default">
            Access could not be loaded. Nothing has changed — this is a problem reading it, not a sign that nobody has
            access.
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {!data.grants_complete && (
            <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              You are seeing only the access you granted yourself. The dashboard owner sees all of it.
            </p>
          )}

          {data.grants.length === 0 && (
            <p className="rounded-lg border border-border-subtle bg-surface-subtle px-3 py-4 text-sm text-content-subtle">
              {data.grants_complete
                ? 'Nobody else has been given access — not a person, not a group, not the organisation. Access is set on the dashboard or on a group card and inherited by everything beneath, so there is nothing to set up card by card.'
                : 'You have not granted access to anyone here. Others may have, but only the owner can see grants they did not make — so this is not the full picture.'}
            </p>
          )}

          {data.cards.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-border-subtle">
              <div
                className={clsx(
                  TABLE_GRID,
                  'border-b border-border-subtle bg-surface-subtle py-2 text-[11px] font-bold uppercase tracking-wider text-content-subtle'
                )}
              >
                <span>Card</span>
                <span className="text-right">{focused ? '' : 'People'}</span>
                <span className="text-right">{focused ? 'Access Level' : 'Access Level'}</span>
                <span className="text-right">Share</span>
              </div>

              {rows.map(({ card, depth, hasChildren }) => {
                const summary = focused ? null : summariseCard(card.id, principals, data.grants, byId);
                const traced = focused
                  ? effectiveAccess(card.id, sourcesOf(focused, data.grants), data.grants, byId)
                  : null;

                const level = focused ? (traced?.role ?? null) : (summary?.topRole ?? null);
                const grantedHere = focused ? !!traced?.grantedHere : !!summary?.grantedHere;
                // Everyone-view faces are everyone who reaches this card, not
                // only those granted here — the count used to include inherited
                // holders while the stack did not, so the two disagreed. A
                // person trace has no stack: the level is theirs by definition.
                const shownHolders = focused ? [] : (summary?.holders ?? []);
                const overflow = shownHolders.slice(3);
                // Where an inherited level was actually granted — the card to
                // open to change it. A grant from above the subtree has no card
                // here to name, and naming the one it sits on would be a title
                // this viewer may have no access to.
                const inheritedFrom =
                  focused && traced && !traced.grantedHere
                    ? traced.fromAbove
                      ? 'a card above'
                      : (byId.get(traced.grantedOn)?.title ?? null)
                    : null;

                // Filtered to one principal the level is theirs, and saying it
                // is the whole point. Across everyone the MAXIMUM is close to
                // useless — creating a dashboard writes an OWNER row for its
                // creator and every card below inherits it, so it reads "Owner"
                // on every row of every dashboard. The summary instead says
                // whether these holders differ, which is the thing worth opening
                // the row for; the exact split is on hover and behind the click.
                const levels = focused ? null : summariseLevels(summary?.holders ?? []);
                const pillLabel = focused
                  ? level === null
                    ? 'No access'
                    : roleLabel(level)
                  : (levels?.label ?? 'No access');
                const pillTitle = focused
                  ? level === null
                    ? undefined
                    : traced?.fromAbove
                      ? `${roleLabel(level)} here, inherited from a card above this one — it can be raised here, but not lowered`
                      : inheritedFrom
                        ? `${roleLabel(level)} here, inherited from the grant on ${inheritedFrom} — it can be raised here, but not lowered`
                        : `${roleLabel(level)}, granted on this card`
                  : levels
                    ? `${levels.breakdown}${
                        levels.varies ? ' — they do not all have the same access' : ''
                      }. ${card.is_shareable ? 'Open the row to see who has what.' : 'Set on its group card.'}`
                    : undefined;

                return (
                  <div
                    key={card.id}
                    className={clsx(
                      TABLE_GRID,
                      'border-b border-border-subtle py-2.5 last:border-b-0',
                      depth === 0 && 'bg-surface-subtle/40',
                      card.is_shareable && 'cursor-pointer transition-colors hover:bg-surface-hover'
                    )}
                    onClick={card.is_shareable ? () => onManageCard?.(card) : undefined}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="shrink-0" style={{ width: depth * 22 }} aria-hidden />

                      {hasChildren ? (
                        <button
                          type="button"
                          aria-label={openCards.has(card.id) ? 'Collapse' : 'Expand'}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggle(card.id);
                          }}
                          className={clsx(
                            'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-content-default transition-transform',
                            openCards.has(card.id) && 'rotate-90'
                          )}
                        >
                          <CaretRightIcon size={15} weight="bold" aria-hidden />
                        </button>
                      ) : (
                        <span className="w-6 shrink-0" aria-hidden />
                      )}

                      <HoverTip
                        title={
                          card.is_shareable
                            ? undefined
                            : 'Access to this card is set on its group card — data and status cards cannot be shared on their own.'
                        }
                      >
                        <span className="min-w-0 truncate text-sm text-content-default">
                          <span className={clsx(depth === 0 ? 'font-bold' : depth === 1 && 'font-semibold')}>
                            {card.title}
                          </span>
                          <span className="mx-1.5 text-slate-400" aria-hidden>
                            ·
                          </span>
                          <span className="text-xs text-content-subtle">
                            {card.node_type === 'group_card'
                              ? card.parent_id === null
                                ? 'Dashboard'
                                : 'Group card'
                              : card.node_type === 'status_card'
                                ? 'Status card'
                                : 'Data card'}
                          </span>
                        </span>
                      </HoverTip>

                      {card.linked_source_title && (
                        <HoverTip
                          title={`Linked from ${card.linked_source_title} — sharing here does not reveal the original`}
                        >
                          <span className="flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-content-subtle">
                            <LockSimpleIcon size={10} aria-hidden />
                            {card.linked_source_title}
                          </span>
                        </HoverTip>
                      )}
                    </span>

                    <span className="flex items-center justify-end gap-1.5">
                      {!focused &&
                        (shownHolders.length > 0 ? (
                          <span className="flex items-center">
                            {shownHolders.slice(0, 3).map(({ principal, access }, index) => (
                              <span key={principal.id} className={clsx(index > 0 && '-ml-1.5')}>
                                <PrincipalAvatar principal={principal} title={holderTooltip(principal, access)} />
                              </span>
                            ))}
                            {overflow.length > 0 && (
                              <HoverTip
                                title={
                                  <span className="flex flex-col gap-0.5">
                                    {overflow.map(({ principal, access }) => (
                                      <span key={principal.id}>{holderTooltip(principal, access)}</span>
                                    ))}
                                  </span>
                                }
                              >
                                <span className="-ml-1.5 inline-flex h-7 shrink-0 items-center justify-center rounded-md border border-border-subtle bg-white px-1.5 text-[10px] font-semibold text-content-subtle">
                                  +{overflow.length}
                                </span>
                              </HoverTip>
                            )}
                          </span>
                        ) : (
                          <HoverTip title="Nobody reaches this card">
                            <span className="inline-flex">
                              <UserCircleDashedIcon size={22} className="text-slate-400" aria-hidden />
                            </span>
                          </HoverTip>
                        ))}
                    </span>

                    <span className="flex justify-end">
                      <LevelPill
                        label={pillLabel}
                        tone={level === null ? 'none' : grantedHere ? 'granted' : 'inherited'}
                        title={pillTitle}
                      />
                    </span>

                    <span className="flex justify-end">
                      {card.is_shareable ? (
                        <Button
                          variant="primary"
                          size="sm"
                          icon={UserCirclePlusIcon}
                          aria-label={`Share ${card.title}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            onManageCard?.(card);
                          }}
                        />
                      ) : null}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {!helpDismissed && (
            <Banner
              variant="info"
              icon={InfoIcon}
              onDismiss={() => {
                window.localStorage.setItem(HELP_DISMISSED_KEY, 'true');
                setHelpDismissed(true);
              }}
            >
              Access is set on the dashboard or a group card and inherited by everything beneath it. Filter to follow
              one person or group, and use Share to change access on that card.
            </Banner>
          )}
        </div>
      )}
    </Modal>
  );
}
